import styles from './experience-signals.module.scss';
import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { usePageTracking } from '../../../hooks/usePageTracking';
import { useAiChatApiService } from '../../../api/providers/AppServiceProvider';
import {
	ExperienceFeedbackDetail,
	ExperienceInsightView,
} from '../../../models/ai-signals/experience-insights';
import ExperienceSignalsHeader from './experience-signals-header';
import ExperienceFeedbackTicketModal from './experience-feedback-ticket-modal';

const FEEDBACK_ID_PATTERN = /^(\d{5,12}|EM-\d+|HC-\d+|[a-f0-9]{24})$/i;
const ARROW_COLLAPSED = '▸';
const ARROW_EXPANDED = '▾';
const ARROW_PREFIX_PATTERN = /^[▸▾]\s*/;

const isFeedbackIdCandidate = (node: Text, text: string) => {
	if (!FEEDBACK_ID_PATTERN.test(text)) {
		return false;
	}
	const parent = node.parentElement;
	if (!parent) {
		return false;
	}
	if (parent.classList.contains('tid') || parent.getAttribute('data-feedback-id')) {
		return false;
	}
	const style = (parent.getAttribute('style') || '').toLowerCase();
	if (style.includes('9ca3af') || style.includes('margin-top:6px') || style.includes('margin-top: 6px')) {
		return true;
	}
	if (/^(EM|HC)-\d+$/i.test(text)) {
		return parent.childNodes.length === 1;
	}
	return parent.childNodes.length === 1 && text.length >= 5;
};

const linkifyFeedbackIds = (root: HTMLElement) => {
	const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
	const textNodes: Text[] = [];
	while (walker.nextNode()) {
		textNodes.push(walker.currentNode as Text);
	}

	textNodes.forEach((node) => {
		const text = node.textContent?.trim() || '';
		if (!isFeedbackIdCandidate(node, text)) {
			return;
		}

		const span = document.createElement('span');
		span.className = 'tid';
		span.textContent = text;
		span.title = 'View feedback details';
		span.setAttribute('data-feedback-id', text);
		node.parentNode?.replaceChild(span, node);
	});
};

const extractReportHtml = (html: string) => {
	const trimmed = html.trim();
	if (!trimmed) {
		return '';
	}

	const template = document.createElement('template');
	template.innerHTML = trimmed;
	template.content.querySelectorAll('script, style, link, meta, title').forEach((node) => node.remove());

	const protoWrap = template.content.querySelector('.proto-wrap');
	if (protoWrap) {
		return (protoWrap as HTMLElement).outerHTML;
	}

	const body = template.content.querySelector('body');
	if (body) {
		return body.innerHTML;
	}

	return template.innerHTML;
};

const setSectionExpanded = (section: HTMLElement, expanded: boolean) => {
	section.classList.toggle('expanded', expanded);
	section.setAttribute('data-expanded', expanded ? 'true' : 'false');
	if (expanded) {
		section.removeAttribute('hidden');
		section.style.display = 'block';
		return;
	}
	section.setAttribute('hidden', '');
	section.style.display = 'none';
};

const setToggleExpanded = (toggle: HTMLElement, expanded: boolean) => {
	const baseText =
		toggle.getAttribute('data-expand-label') ||
		(toggle.textContent || '').replace(ARROW_PREFIX_PATTERN, '').replace(/\s+/g, ' ').trim();
	toggle.setAttribute('data-expand-label', baseText);
	toggle.setAttribute('data-expanded', expanded ? 'true' : 'false');
	toggle.classList.toggle('open', expanded);
	toggle.style.cursor = 'pointer';
	toggle.style.userSelect = 'none';
	toggle.textContent = `${expanded ? ARROW_EXPANDED : ARROW_COLLAPSED} ${baseText}`;
};

const enhanceInsightHtml = (html: string) => {
	const template = document.createElement('template');
	template.innerHTML = extractReportHtml(html);

	template.content.querySelectorAll<HTMLElement>('[data-expand-section]').forEach((section) => {
		const sectionId = (section.getAttribute('data-expand-section') || '').trim();
		if (!sectionId || sectionId.includes('${')) {
			section.remove();
			return;
		}
		setSectionExpanded(section, false);
	});

	template.content.querySelectorAll<HTMLElement>('[data-expand-toggle]').forEach((toggle) => {
		const sectionId = (toggle.getAttribute('data-expand-toggle') || '').trim();
		if (!sectionId || sectionId.includes('${')) {
			toggle.remove();
			return;
		}
		toggle.setAttribute('data-expand-toggle', sectionId);
		setToggleExpanded(toggle, false);
	});

	return template.innerHTML;
};

const ExperienceSignals: FC = () => {
	usePageTracking('Page Viewed', 'Experience Signals');
	const aiChatApiService = useAiChatApiService();
	const requestIdRef = useRef(0);
	const hasLoadedRef = useRef(false);
	const reportRef = useRef<HTMLDivElement>(null);

	const [views, setViews] = useState<ExperienceInsightView[]>([]);
	const [activeViewType, setActiveViewType] = useState<string | null>(null);
	const [selectedDate, setSelectedDate] = useState('');
	const [insightHtml, setInsightHtml] = useState<string | null>(null);
	const [isLoading, setIsLoading] = useState(true);
	const [errorMessage, setErrorMessage] = useState<string | null>(null);

	const [selectedFeedbackId, setSelectedFeedbackId] = useState<string | null>(null);
	const [feedbackDetail, setFeedbackDetail] = useState<ExperienceFeedbackDetail | null>(null);
	const [isFeedbackLoading, setIsFeedbackLoading] = useState(false);
	const [feedbackError, setFeedbackError] = useState<string | null>(null);

	const renderedHtml = useMemo(() => (insightHtml ? enhanceInsightHtml(insightHtml) : null), [insightHtml]);

	const loadSignals = useCallback(
		async (viewType?: string | null, reportDate?: string | null) => {
			const requestId = ++requestIdRef.current;
			setIsLoading(true);
			setErrorMessage(null);

			try {
				const response = await aiChatApiService.getExperienceSignals({
					viewType: viewType || undefined,
					reportDate: reportDate || undefined,
				});

				if (requestId !== requestIdRef.current) {
					return;
				}

				setViews(response.views);
				setActiveViewType(viewType || response.viewType || response.views[0]?.viewType || null);
				setSelectedDate(reportDate || response.reportDate || '');
				setInsightHtml(response.insightHtml ?? null);
			} catch {
				if (requestId !== requestIdRef.current) {
					return;
				}
				setInsightHtml(null);
				setErrorMessage('Unable to load experience signals.');
			} finally {
				if (requestId === requestIdRef.current) {
					setIsLoading(false);
				}
			}
		},
		[aiChatApiService]
	);

	useEffect(() => {
		if (hasLoadedRef.current) {
			return;
		}
		hasLoadedRef.current = true;
		loadSignals();
	}, [loadSignals]);

	const openFeedback = useCallback(
		async (feedbackId: string) => {
			setSelectedFeedbackId(feedbackId);
			setFeedbackDetail(null);
			setFeedbackError(null);
			setIsFeedbackLoading(true);
			try {
				const detail = await aiChatApiService.getExperienceFeedbackDetail(feedbackId);
				setFeedbackDetail(detail);
			} catch {
				setFeedbackError('Feedback not found');
			} finally {
				setIsFeedbackLoading(false);
			}
		},
		[aiChatApiService]
	);

	useEffect(() => {
		if (!renderedHtml || !reportRef.current) {
			return;
		}
		linkifyFeedbackIds(reportRef.current);
	}, [renderedHtml]);

	const handleReportClick = (event: React.MouseEvent<HTMLDivElement>) => {
		const target = event.target as HTMLElement | null;
		if (!target) {
			return;
		}

		const feedbackEl = target.closest<HTMLElement>('[data-feedback-id]');
		if (feedbackEl && event.currentTarget.contains(feedbackEl)) {
			const feedbackId = (feedbackEl.getAttribute('data-feedback-id') || '').trim();
			if (feedbackId) {
				event.preventDefault();
				event.stopPropagation();
				openFeedback(feedbackId);
				return;
			}
		}

		const toggle = target.closest<HTMLElement>('[data-expand-toggle]');
		if (!toggle || !event.currentTarget.contains(toggle)) {
			return;
		}

		const sectionId = (toggle.getAttribute('data-expand-toggle') || '').trim();
		if (!sectionId) {
			return;
		}

		const section = Array.from(
			event.currentTarget.querySelectorAll<HTMLElement>('[data-expand-section]')
		).find((node) => (node.getAttribute('data-expand-section') || '').trim() === sectionId);

		if (!section) {
			return;
		}

		event.preventDefault();
		event.stopPropagation();

		const nextExpanded = toggle.getAttribute('data-expanded') !== 'true';
		setToggleExpanded(toggle, nextExpanded);
		setSectionExpanded(section, nextExpanded);
	};

	const handleViewChange: React.Dispatch<React.SetStateAction<string | null>> = (value) => {
		const nextViewType = typeof value === 'function' ? value(activeViewType) : value;
		if (!nextViewType || nextViewType === activeViewType) {
			return;
		}
		setActiveViewType(nextViewType);
		loadSignals(nextViewType, selectedDate || undefined);
	};

	const handleDateChange: React.Dispatch<React.SetStateAction<string>> = (value) => {
		const nextReportDate = typeof value === 'function' ? value(selectedDate) : value;
		if (!nextReportDate || nextReportDate === selectedDate) {
			return;
		}
		setSelectedDate(nextReportDate);
		loadSignals(activeViewType, nextReportDate);
	};

	const closeFeedback = () => {
		setSelectedFeedbackId(null);
		setFeedbackDetail(null);
		setFeedbackError(null);
		setIsFeedbackLoading(false);
	};

	return (
		<div className={styles.page}>
			<ExperienceSignalsHeader
				views={views}
				activeViewType={activeViewType}
				selectedDate={selectedDate}
				onViewChange={handleViewChange}
				onDateChange={handleDateChange}
			/>
			<div className={styles.report_area}>
				{isLoading ? (
					<div className={styles.empty_state}>
						<p>Loading report...</p>
					</div>
				) : errorMessage ? (
					<div className={styles.empty_state}>
						<p>{errorMessage}</p>
					</div>
				) : renderedHtml ? (
					<div
						ref={reportRef}
						className={styles.report_wrap}
						onClick={handleReportClick}
						dangerouslySetInnerHTML={{ __html: renderedHtml }}
					/>
				) : (
					<div className={styles.empty_state}>
						<svg viewBox="0 0 48 48" fill="none" stroke="currentColor" strokeWidth="1.4" aria-hidden>
							<rect x="8" y="10" width="32" height="28" rx="4" />
							<path d="M16 20h16M16 26h12M16 32h8" />
						</svg>
						<p>No report available for this date</p>
					</div>
				)}
			</div>
			{selectedFeedbackId ? (
				<ExperienceFeedbackTicketModal
					feedbackId={selectedFeedbackId}
					detail={feedbackDetail}
					isLoading={isFeedbackLoading}
					errorMessage={feedbackError}
					onClose={closeFeedback}
				/>
			) : null}
		</div>
	);
};

export default ExperienceSignals;
